import {
  Button,
  Typography,
  Box,
  Container,
  Fade,
  Zoom,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import client from 'client';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useDispatch } from 'react-redux';
import FuseLoading from '@fuse/core/FuseLoading';
import store from 'app/store';
import EmailConfigEntry from './EmailConfigEntry';
import {
  Email as EmailIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  ListAlt as ListAltIcon,
  ToggleOn as ToggleOnIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  minHeight: '100%',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}10 100%)`,
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const SaveButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: theme.spacing(2),
  textTransform: 'none',
  boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`,
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const EmptyStateIcon = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.secondary.light}20 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  '& svg': {
    fontSize: 60,
    color: theme.palette.text.secondary,
  },
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.9),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
    '& fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.2),
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const EmailConfiguration = () => {
  const { t } = useTranslation('navigation');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState({});
  const [currentLevel, setCurrentLevel] = useState('company_level');
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    setEmails({});
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const result = await client.post('/props/get-admin-comms', {
        type: 'emails',
        id: store.getState().loadz_auth.AuthReducer.user.user_id,
      });
      console.log('Emails', result.data);
      if (result.data.status == false) {
        showError('error', t('ERROR_ADMIN_MISSING'));
        setEmails({});
        setLoading(false);
      } else {
        setEmails(result.data);
        const levels = Object.keys(result.data || {});
        if (levels.length && !levels.includes(currentLevel)) {
          setCurrentLevel(levels[0]);
        }
        setLoading(false);
      }
    } catch (e) {
      setEmails({});
      setLoading(false);
      console.log(e);
    }
  };

  const onChange = (idx, new_emails) => {
    let newEmails = JSON.parse(JSON.stringify(emails));
    newEmails[currentLevel][idx].data = [...new_emails];
    setEmails(newEmails);
  };

  const onRemove = (idx, email) => {
    let newEmails = JSON.parse(JSON.stringify(emails));
    newEmails[currentLevel][idx].data = newEmails[currentLevel][
      idx
    ].data.filter((e) => e !== email);
    setEmails(newEmails);
  };

  const changeEnabled = (idx, enabled) => {
    let newEmails = JSON.parse(JSON.stringify(emails));
    newEmails[currentLevel][idx].enabled = enabled;
    setEmails(newEmails);
  };

  const changeSendIfEmpty = (idx, send_if_empty) => {
    let newEmails = JSON.parse(JSON.stringify(emails));
    newEmails[currentLevel][idx].send_if_empty = send_if_empty;
    setEmails(newEmails);
  };

  const updateEmails = async () => {
    try {
      setSaving(true);
      const result = await client.post('/props/update-admin-comms', {
        type: 'emails',
        id: store.getState().loadz_auth.AuthReducer.user.user_id,
        data: emails,
      });
      if (result.data.status == true) {
        showError('success', t('EMAILS_UPDATED'));
      } else {
        showError('error', t('ERROR_UPDATING_EMAILS'));
      }
      setSaving(false);
    } catch (e) {
      setSaving(false);
      console.log(e);
      showError('error', t('ERROR_UPDATING_EMAILS'));
    }
  };

  const showError = (status, message) => {
    return dispatch(
      showMessage({
        message: message,
        autoHideDuration: 6000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        variant: status,
      })
    );
  };

  const nameGenerator = (key) => {
    let name = key.replaceAll('_', ' ');
    let name_split = name.split(' ');
    let name_final = '';
    name_split.forEach((word) => {
      name_final += word.charAt(0).toUpperCase() + word.slice(1) + ' ';
    });
    return name_final.trim();
  };

  const levelLabel = (level) => {
    if (level === 'super_admin_level') return t('SUPER_ADMIN_LEVEL');
    if (level === 'company_level') return t('COMPANY_LEVEL');
    return nameGenerator(level);
  };

  const availableLevels = Object.keys(emails || {});

  const filterEmails = (emailList, query) => {
    if (!query || query.trim() === '') {
      return emailList;
    }

    const searchTerm = query.toLowerCase().trim();
    return emailList.filter((emailGroup) => {
      const name = nameGenerator(emailGroup.type || '').toLowerCase();
      const type = (emailGroup.type || '').toLowerCase();
      const description = emailGroup.description?.toLowerCase() || '';

      return (
        name.includes(searchTerm) ||
        type.includes(searchTerm) ||
        description.includes(searchTerm)
      );
    });
  };

  const filteredEmails = emails?.[currentLevel]
    ? filterEmails(emails[currentLevel], searchQuery)
    : [];

  if (loading) {
    return (
      <Box
        className='flex flex-row justify-center align-center items-center w-full h-full'
        sx={{ minHeight: '60vh' }}
      >
        <FuseLoading />
      </Box>
    );
  }

  if (!emails?.[currentLevel]?.length || emails?.[currentLevel]?.length === 0) {
    return (
      <Fade in={true} timeout={500}>
        <EmptyStateContainer>
          <EmptyStateIcon>
            <EmailIcon />
          </EmptyStateIcon>
          <Typography variant='h5' fontWeight={600} gutterBottom>
            {t('NO_CONFIGURATION_FOUND')}
          </Typography>
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ maxWidth: 400 }}
          >
            {t('NO_EMAIL_CONFIGURATIONS_AVAILABLE')}
          </Typography>
        </EmptyStateContainer>
      </Fade>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <StyledContainer maxWidth='xl'>
        <HeaderSection>
          <Box display='flex' alignItems='center' gap={2} mb={2.5}>
            <SettingsIcon color='primary' sx={{ fontSize: 32 }} />
            <Box flex={1}>
              <Typography variant='h4' fontWeight={700} gutterBottom>
                {t('EMAIL_CONFIGURATION_TITLE')}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('EMAIL_CONFIGURATION_SUBTITLE')}
              </Typography>
            </Box>
            <Tooltip
              title={
                <Box sx={{ maxWidth: 500, p: 1 }}>
                  <Box display='flex' alignItems='center' gap={1.5} mb={2}>
                    <InfoIcon color='info' sx={{ fontSize: 24 }} />
                    <Typography variant='h6' fontWeight={700} color='info.main'>
                      {t('EMAIL_CONFIG_WHAT_CAN_YOU_DO')}
                    </Typography>
                  </Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2.5, lineHeight: 1.8 }}
                  >
                    {t('EMAIL_CONFIG_INTRO_TEXT')}
                  </Typography>

                  <Box>
                    <Box
                      display='flex'
                      alignItems='flex-start'
                      gap={1.5}
                      sx={{
                        padding: 1.5,
                        borderRadius: 1,
                        background: alpha('#fff', 0.1),
                        marginBottom: 1,
                      }}
                    >
                      <NotificationsIcon
                        sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }}
                      />
                      <Box>
                        <Typography
                          variant='subtitle2'
                          fontWeight={600}
                          gutterBottom
                        >
                          {t('EMAIL_CONFIG_CONFIGURE_ADDRESSES')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {t('EMAIL_CONFIG_CONFIGURE_ADDRESSES_DESC')}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      display='flex'
                      alignItems='flex-start'
                      gap={1.5}
                      sx={{
                        padding: 1.5,
                        borderRadius: 1,
                        background: alpha('#fff', 0.1),
                        marginBottom: 1,
                      }}
                    >
                      <ToggleOnIcon
                        sx={{ fontSize: 20, color: 'success.main', mt: 0.5 }}
                      />
                      <Box>
                        <Typography
                          variant='subtitle2'
                          fontWeight={600}
                          gutterBottom
                        >
                          {t('EMAIL_CONFIG_TOGGLE_NOTIFICATIONS')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {t('EMAIL_CONFIG_TOGGLE_NOTIFICATIONS_DESC')}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      display='flex'
                      alignItems='flex-start'
                      gap={1.5}
                      sx={{
                        padding: 1.5,
                        borderRadius: 1,
                        background: alpha('#fff', 0.1),
                        marginBottom: 1,
                      }}
                    >
                      <ListAltIcon
                        sx={{ fontSize: 20, color: 'info.main', mt: 0.5 }}
                      />
                      <Box>
                        <Typography
                          variant='subtitle2'
                          fontWeight={600}
                          gutterBottom
                        >
                          {t('EMAIL_CONFIG_TEMPLATES')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {t('EMAIL_CONFIG_TEMPLATES_DESC')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Alert
                    severity='info'
                    icon={<InfoIcon />}
                    sx={{
                      mt: 2.5,
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        width: '100%',
                      },
                    }}
                  >
                    <Typography variant='body2' fontWeight={600} gutterBottom>
                      {t('EMAIL_CONFIG_TIP')}
                    </Typography>
                    <Typography variant='body2'>
                      {t('EMAIL_CONFIG_TIP_TEXT')}
                    </Typography>
                  </Alert>
                </Box>
              }
              arrow
              placement='bottom-end'
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: `1px solid ${alpha('#000', 0.1)}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    maxWidth: 600,
                    p: 0,
                  },
                },
                arrow: {
                  sx: {
                    color: 'background.paper',
                    '&::before': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                  },
                },
              }}
            >
              <IconButton
                sx={{
                  color: 'primary.main',
                  background: alpha('#fff', 0.1),
                  '&:hover': {
                    background: alpha('#fff', 0.2),
                  },
                }}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {availableLevels.length > 1 && (
            <Tabs
              value={currentLevel}
              onChange={(_event, value) => setCurrentLevel(value)}
              sx={{ mb: 2 }}
              variant='scrollable'
              scrollButtons='auto'
            >
              {availableLevels.map((level) => (
                <Tab key={level} value={level} label={levelLabel(level)} />
              ))}
            </Tabs>
          )}
          <SearchTextField
            fullWidth
            placeholder={t('SEARCH_EMAIL_CONFIGURATIONS')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    onClick={() => setSearchQuery('')}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </HeaderSection>

        {searchQuery && filteredEmails.length === 0 && (
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            py={4}
            mb={3}
          >
            <Typography variant='body1' color='text.secondary' gutterBottom>
              {t('NO_RESULTS_FOUND')} "{searchQuery}"
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {t('TRY_OTHER_SEARCH_TERMS')}
            </Typography>
          </Box>
        )}

        <GridContainer>
          {filteredEmails.map((emailGroup, idx) => {
            // Encontrar el índice original en el array completo
            const originalIndex =
              emails[currentLevel]?.findIndex(
                (item) => item.type === emailGroup.type
              ) ?? idx;

            return (
              <Zoom
                in={true}
                timeout={300}
                style={{ transitionDelay: `${idx * 50}ms` }}
                key={`email-group-${originalIndex}-${emailGroup.type}`}
              >
                <Box>
                  <EmailConfigEntry
                    emailGroup={emailGroup}
                    email_name={emailGroup.type}
                    index={originalIndex}
                    onChange={onChange}
                    onRemove={onRemove}
                    changeEnabled={changeEnabled}
                    changeSendIfEmpty={changeSendIfEmpty}
                  />
                </Box>
              </Zoom>
            );
          })}
        </GridContainer>

        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          gap={2}
          mt={4}
          mb={4}
        >
          <SaveButton
            variant='contained'
            color='primary'
            size='large'
            startIcon={
              saving ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <SaveIcon />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              updateEmails();
            }}
            disabled={saving}
          >
            {saving ? t('SAVING') : t('UPDATE')}
          </SaveButton>
        </Box>
      </StyledContainer>
    </Fade>
  );
};

export default EmailConfiguration;
